import {
  Card,
  CardContent,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useState, useCallback, useEffect } from "react";
import { SEO } from "@/components/SEO";
import useEmblaCarousel from "embla-carousel-react";

const Gallery = () => {
  const [emblaRef, emblaApi] = useEmblaCarousel({ 
    loop: true,
    slidesToScroll: 1,
    breakpoints: {
      '(min-width: 768px)': { slidesToScroll: 2 },
      '(min-width: 1024px)': { slidesToScroll: 3 }
    }
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    
    // Auto-play functionality
    const autoPlayInterval = setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollTo(0);
      }
    }, 7000); // Change slide every 7 seconds
    
    return () => {
      clearInterval(autoPlayInterval);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const images = [
    {
      src: "/lovable-uploads/c36d1a65-ee9d-42e7-af39-e358d9fef38a.png",
      description: "RAM 2500 Exterior",
      width: 1200,
      height: 800,
      alt: "Professional exterior detailing of a RAM 2500 truck showing pristine finish and shine"
    },
    {
      src: "/lovable-uploads/3b5f97c0-eedf-48f0-a5f1-bbb459c711ed.png",
      description: "RAM 2500 After Detail",
      width: 1200,
      height: 800,
      alt: "After shot of RAM 2500 detailing showcasing immaculate paint correction and wax finish"
    },
    {
      src: "/lovable-uploads/824ab25b-b4cf-4fbe-887f-c3c9fca5c9fa.png",
      description: "Interior Before",
      width: 1200,
      height: 800,
      alt: "Vehicle interior before professional detailing showing areas needing attention"
    },
    {
      src: "/lovable-uploads/fb6b92f8-898e-4133-8bd5-a5e984ba3996.png",
      description: "Interior After",
      width: 1200,
      height: 800,
      alt: "Transformed vehicle interior after professional detailing showing pristine surfaces"
    },
    {
      src: "/lovable-uploads/6bcb7df9-f382-4788-9e60-8f930d0b87c1.png",
      description: "Original Seat Condition",
      width: 1200,
      height: 800,
      alt: "Vehicle seat before professional cleaning and restoration treatment"
    },
    {
      src: "/lovable-uploads/8df4231c-5c16-47c2-bf7d-04759f3eae7a.png",
      description: "Restored Seat",
      width: 1200,
      height: 800,
      alt: "Professionally restored vehicle seat showing remarkable improvement in condition"
    }
  ];

  // Structured data for the image gallery
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ImageGallery",
    "name": "Mobile Detail Co. Work Gallery",
    "description": "Professional auto detailing results showcasing our work in the Twin Cities area",
    "image": images.map(img => ({
      "@type": "ImageObject",
      "contentUrl": `https://mobiledetailco.com${img.src}`,
      "description": img.alt,
      "width": img.width,
      "height": img.height
    }))
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-primary-light pt-24">
      <SEO
        title="Auto Detailing Gallery | Before & After Results | Mobile Detail Co."
        description="View our professional auto detailing results. Before and after photos showcasing our premium detailing services in the Twin Cities area."
        url="https://mobiledetailco.com/gallery"
        jsonLd={structuredData}
      />
      
      <section className="py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-6xl font-bold text-primary text-center mb-12">
            Our Work
          </h1>
          
          <div className="slider-container relative overflow-hidden">
            <div className="embla overflow-hidden" ref={emblaRef}>
              <div className="embla__container flex">
                {images.map((image, index) => (
                  <div
                    key={index}
                    className="embla__slide relative min-w-full md:min-w-[50%] lg:min-w-[33.333%] pl-4"
                  >
                    <Card className="overflow-hidden h-full">
                      <CardContent className="p-4 h-full">
                        <Dialog>
                          <DialogTrigger asChild>
                            <div className="cursor-pointer transition-transform hover:scale-105 h-full flex flex-col">
                              <div className="aspect-w-16 aspect-h-9 flex-grow">
                                <img
                                  src={image.src}
                                  alt={image.alt}
                                  width={image.width}
                                  height={image.height}
                                  loading="lazy"
                                  className="w-full h-full object-cover rounded-lg"
                                />
                              </div>
                              <p className="mt-2 text-sm text-gray-600 font-medium">{image.description}</p>
                            </div>
                          </DialogTrigger>
                          <DialogContent className="max-w-4xl w-full p-0">
                            <img
                              src={image.src}
                              alt={image.alt}
                              width={image.width}
                              height={image.height}
                              className="w-full h-auto max-h-[80vh] object-contain"
                            />
                          </DialogContent>
                        </Dialog>
                      </CardContent>
                    </Card>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Slider Dots */}
            <div className="embla__dots mt-8 flex justify-center gap-2 z-30">
              {scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  className={`embla__dot w-3 h-3 rounded-full transition-all ${
                    index === selectedIndex
                      ? "bg-primary scale-125"
                      : "bg-gray-300 hover:bg-gray-400"
                  }`}
                  type="button"
                  onClick={() => scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
